// src/mobile_pages/const/DailyEditor.jsx
import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Calendar,
  Cloud,
  FileText,
  Users,
  Package,
  Wrench,
  Plus,
  Trash2,
  ClipboardList,
  History,
  Paperclip
} from "lucide-react";

import {
  getWorkReportDetail,
  getProjectDailyContext,
  createWorkReport,
  updateWorkReport,
  getWorkDetails,
  getWorkReports,
} from "../../api/const";
import { getProjectName } from "../../api/project";
import ReferenceModal from "../../components/approval/ReferenceModal";
import MobileAttachmentBox from "../../components/common/papers/MobileAttachmentBox";

export default function DailyEditor() {
  const navigate = useNavigate();
  const { id: projectId, dns } = useParams();
  const isEditMode = Boolean(dns);
  const attachmentRef = useRef(null);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);

  const [projectName, setProjectName] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const rawUser = localStorage.getItem("user");
  const user = rawUser ? JSON.parse(rawUser) : null;
  const [created_by, setCreatedBy] = useState(user?.name || "");

  const [confirmer, setConfirmer] = useState("");
  const [confirmer_id, setConfirmerId] = useState("");
  const [showConfirmerModal, setShowConfirmerModal] = useState(false);
  const [confirmerIds, setConfirmerIds] = useState([]);

  const [weather, setWeather] = useState("");
  const [tempMin, setTempMin] = useState("");
  const [tempMax, setTempMax] = useState("");
  const [tempAvg, setTempAvg] = useState("");
  const [precipAvg, setPrecipAvg] = useState("");
  const [precipTotal, setPrecipTotal] = useState("");
  const [snowAvg, setSnowAvg] = useState("");
  const [snowTotal, setSnowTotal] = useState("");
  const [windAvg, setWindAvg] = useState("");
  const [windMax, setWindMax] = useState("");

  const [todayWork, setTodayWork] = useState("");
  const [tomorrowWork, setTomorrowWork] = useState("");

  // 상세작업 관련
  const [existingDetails, setExistingDetails] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const [materialData, setMaterialData] = useState([]);
  const [equipmentData, setEquipmentData] = useState([]);
  const [personnelData, setPersonnelData] = useState([]);

  // 이전 작업 불러오기 관련
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);

  // 섹션 접힘 상태
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    weather: true,
    work: true,
    material: false,
    equipment: false,
    personnel: false,
    attachment: false,
  });

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /* ============================================
     상세작업 불러오기
  ============================================= */
  useEffect(() => {
    if (!date) return;
    (async () => {
      try {
        const data = await getWorkDetails(date);
        setExistingDetails(data.results || []);
      } catch (err) {
        console.error("상세작업 불러오기 실패", err);
      }
    })();
  }, [date]);

  // 상세작업 적용
  const applySelectedDetails = () => {
    const text = existingDetails
      .filter((w) => selectedActivities.includes(w.activity))
      .map((w) => {
        if (w.content) {
          const lines = w.content.split("\n").filter((l) => l.trim() !== "");
          const bullets = lines.map((l) => `- ${l}`).join("\n");
          return `${w.activity_name}\n${bullets}`;
        } else {
          return w.activity_name;
        }
      })
      .join("\n\n");

    setTodayWork((prev) => (prev ? prev + "\n\n" + text : text));
    setShowDetailModal(false);
  };

  /* ============================================
     이전 작업 불러오기
  ============================================= */
  const openHistoryModal = async () => {
    setShowHistoryModal(true);
    setHistoryLoading(true);
    try {
      const data = await getWorkReports({
        project: projectId,
        page_size: 50,
      });
      setHistoryRows(data.results ?? []);
    } catch (err) {
      console.error(err);
      alert("이전 공사일보 목록을 불러오지 못했습니다.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSelectHistory = async (row) => {
    try {
      const detail = await getWorkReportDetail(row.dns);
      const meta = detail.meta || {};

      // 폼 데이터 채우기 (날짜는 현재 날짜 유지)
      setConfirmer(detail.confirmer || "");
      setConfirmerId(meta.confirmer_id || "");
      setWeather(meta.weather || "");
      setTempMin(meta.temp_min ?? "");
      setTempMax(meta.temp_max ?? "");
      setTempAvg(meta.temp_avg ?? "");
      setPrecipAvg(meta.precip_avg ?? "");
      setPrecipTotal(meta.precip_total ?? "");
      setSnowAvg(meta.snow_avg ?? "");
      setSnowTotal(meta.snow_total ?? "");
      setWindAvg(meta.wind_speed_avg ?? "");
      setWindMax(meta.wind_speed_max ?? "");
      setTodayWork(meta.today_work || "");
      setTomorrowWork(meta.tomorrow_work || "");
      setSelectedActivities(meta.activities || []);

      // 자재/장비/인원 데이터 채우기
      setMaterialData(
        (detail.materials || []).map((m) => ({
          id: m.id,
          name: m.name,
          spec: m.spec,
          unit: m.unit,
          prev_total: 0,
          today: m.qty,
        }))
      );
      setEquipmentData(
        (detail.equipments || []).map((e) => ({
          id: e.id,
          name: e.name,
          spec: e.spec,
          prev_total: 0,
          today: e.qty,
        }))
      );
      setPersonnelData(
        (detail.personnels || []).map((p) => ({
          id: p.id,
          work_type: p.work_type,
          category: p.category,
          prev_total: 0,
          today: p.qty,
        }))
      );

      setShowHistoryModal(false);
      alert("이전 작업 정보를 불러왔습니다.");
    } catch (err) {
      console.error(err);
      alert("이전 작업 정보를 불러오지 못했습니다.");
    }
  };

  /* ============================================
     EDIT MODE: 상세 불러오기
  ============================================= */
  useEffect(() => {
    if (!isEditMode) return;

    (async () => {
      try {
        const data = await getWorkReportDetail(dns);
        const res = await getProjectName(projectId);

        setProjectName(res.name || "");
        setDate(data.meta.date || new Date().toISOString().slice(0, 10));
        setCreatedBy(data.created_by.name || "");
        setConfirmer(data.confirmer || "");
        setConfirmerId(data.meta.confirmer_id || "");

        setWeather(data.meta.weather || "");
        setTempMin(data.meta.temp_min ?? "");
        setTempMax(data.meta.temp_max ?? "");
        setTempAvg(data.meta.temp_avg ?? "");
        setPrecipAvg(data.meta.precip_avg ?? "");
        setPrecipTotal(data.meta.precip_total ?? "");
        setSnowAvg(data.meta.snow_avg ?? "");
        setSnowTotal(data.meta.snow_total ?? "");
        setWindAvg(data.meta.wind_speed_avg ?? "");
        setWindMax(data.meta.wind_speed_max ?? "");

        setTodayWork(data.meta.today_work || "");
        setTomorrowWork(data.meta.tomorrow_work || "");
        setSelectedActivities(data.meta.activities || []);

        setMaterialData(
          (data.materials || []).map((m) => ({
            id: m.id,
            name: m.name,
            spec: m.spec,
            unit: m.unit,
            prev_total: 0, // API에서 가져오거나 계산 필요
            today: m.qty,
          }))
        );

        setEquipmentData(
          (data.equipments || []).map((e) => ({
            id: e.id,
            name: e.name,
            spec: e.spec,
            prev_total: 0,
            today: e.qty,
          }))
        );

        setPersonnelData(
          (data.personnels || []).map((p) => ({
            id: p.id,
            work_type: p.work_type,
            category: p.category,
            prev_total: 0,
            today: p.qty,
          }))
        );
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [dns, isEditMode]);

  /* ============================================
     CREATE MODE: 초기 날씨 자동 로드
  ============================================= */
  useEffect(() => {
    if (isEditMode) return;

    (async () => {
      try {
        const ctx = await getProjectDailyContext(projectId);
        if (!ctx) return;

        setProjectName(ctx.pjt_nm ?? "");
        setWeather(ctx.summary || "");
        setTempMin(ctx.min_temp?.toFixed(1) ?? "");
        setTempMax(ctx.max_temp?.toFixed(1) ?? "");
        setTempAvg(ctx.avg_temp?.toFixed(1) ?? "");
        setPrecipAvg(ctx.precip_avg?.toFixed(1) ?? "");
        setPrecipTotal(ctx.precip_total?.toFixed(1) ?? "");
        setSnowAvg(ctx.snow_avg?.toFixed(1) ?? "");
        setSnowTotal(ctx.snow_total?.toFixed(1) ?? "");
        setWindAvg(ctx.wind_speed_avg?.toFixed(1) ?? "");
        setWindMax(ctx.wind_speed_max?.toFixed(1) ?? "");
      } catch (err) {
        console.warn(err);
      }
    })();
  }, [projectId, isEditMode]);

  /* ============================================
     저장 처리
  ============================================= */
  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const meta = {
        projectName,
        date,
        confirmer_id: confirmer_id || null,
        weather,
        temp_min: tempMin ?? null,
        temp_max: tempMax ?? null,
        temp_avg: tempAvg ?? null,
        precip_total: precipTotal ?? null,
        precip_avg: precipAvg ?? null,
        snow_total: snowTotal ?? null,
        snow_avg: snowAvg ?? null,
        wind_speed_avg: windAvg ?? null,
        wind_speed_max: windMax ?? null,
        today_work: todayWork,
        tomorrow_work: tomorrowWork,
        activities: selectedActivities,
        materials: materialData.map((m) => ({
          material: { name: m.name, spec: m.spec, unit: m.unit },
          qty: Number(m.today) || 0,
        })),
        equipments: equipmentData.map((e) => ({
          equipment: { name: e.name, spec: e.spec },
          qty: Number(e.today) || 0,
        })),
        personnels: personnelData.map((p) => ({
          personnel: { work_type: p.work_type, category: p.category },
          qty: Number(p.today) || 0,
        })),
      };

      let result;
      if (dns) {
        result = await updateWorkReport(dns, meta);
      } else {
        result = await createWorkReport(meta);
      }

      const newDns = result?.dns || result?.doc_dns || dns;

      // 첨부파일 저장
      if (newDns && attachmentRef.current) {
        await attachmentRef.current.save(newDns);
      }

      alert("저장 완료!");
      navigate(`/project/${projectId}/const/daily`);
    } catch (err) {
      console.error("저장 실패:", err);
      alert("저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => navigate(`/project/${projectId}/const/daily`);

  // 자재 추가/삭제/수정
  const addMaterial = () => setMaterialData([...materialData, { name: "", spec: "", unit: "", prev_total: 0, today: 0 }]);
  const removeMaterial = (idx) => setMaterialData(materialData.filter((_, i) => i !== idx));
  const updateMaterial = (idx, field, value) => {
    const next = [...materialData];
    next[idx][field] = value;
    setMaterialData(next);
  };

  // 장비 추가/삭제/수정
  const addEquipment = () => setEquipmentData([...equipmentData, { name: "", spec: "", prev_total: 0, today: 0 }]);
  const removeEquipment = (idx) => setEquipmentData(equipmentData.filter((_, i) => i !== idx));
  const updateEquipment = (idx, field, value) => {
    const next = [...equipmentData];
    next[idx][field] = value;
    setEquipmentData(next);
  };

  // 인원 추가/삭제/수정
  const addPersonnel = () => setPersonnelData([...personnelData, { work_type: "", category: "", prev_total: 0, today: 0 }]);
  const removePersonnel = (idx) => setPersonnelData(personnelData.filter((_, i) => i !== idx));
  const updatePersonnel = (idx, field, value) => {
    const next = [...personnelData];
    next[idx][field] = value;
    setPersonnelData(next);
  };

  // 요약 정보
  const summary = useMemo(() => ({
    materialCount: materialData.filter(m => m.name).length,
    equipmentCount: equipmentData.filter(e => e.name).length,
    personnelCount: personnelData.reduce((acc, p) => acc + (Number(p.today) || 0), 0),
  }), [materialData, equipmentData, personnelData]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-[15px] text-slate-400">불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 모바일 본문 영역 */}
      <main className="px-4 pt-4 pb-24">
        {/* 페이지 타이틀 */}
        <header className="mb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">시공관리 · 공사일보</p>
              <h2 className="text-xl font-bold text-gray-900">
                {isEditMode ? "공사일보 수정" : "공사일보 작성"}
              </h2>
            </div>
            {!isEditMode && (
              <button
                type="button"
                onClick={openHistoryModal}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 active:scale-[0.97] transition-all"
              >
                <History size={14} />
                이전 작업 불러오기
              </button>
            )}
          </div>
        </header>

        <div className="space-y-3">
          {/* 1. 기본 정보 */}
          <Section
            title="기본 정보"
            expanded={expandedSections.basic}
            onToggle={() => toggleSection("basic")}
            icon={<Calendar size={16} className="text-blue-500" />}
          >
            <InputField
              label="현장명"
              value={projectName}
              disabled
            />
            <InputField
              label="작성일"
              type="date"
              value={date}
              onChange={(v) => setDate(v)}
            />
            <InputField
              label="작성자"
              value={created_by}
              disabled
            />
            <div>
              <label className="text-gray-500 text-xs mb-1 block">확인자</label>
              <input
                type="text"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none cursor-pointer hover:bg-gray-50"
                value={confirmer || "선택하세요"}
                readOnly
                onClick={() => setShowConfirmerModal(true)}
              />
            </div>
          </Section>

          {/* 2. 기상 정보 */}
          <Section
            title="기상 정보"
            expanded={expandedSections.weather}
            onToggle={() => toggleSection("weather")}
            icon={<Cloud size={16} className="text-sky-500" />}
          >
            <InputField
              label="날씨"
              value={weather}
              onChange={(v) => setWeather(v)}
              placeholder="예: 맑음"
            />
            <div className="grid grid-cols-2 gap-3">
              <InputField
                label="최저 기온 (°C)"
                type="number"
                value={tempMin}
                onChange={(v) => setTempMin(v)}
              />
              <InputField
                label="최고 기온 (°C)"
                type="number"
                value={tempMax}
                onChange={(v) => setTempMax(v)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <InputField
                label="강수량 (mm)"
                type="number"
                value={precipAvg}
                onChange={(v) => setPrecipAvg(v)}
              />
              <InputField
                label="적설량 (cm)"
                type="number"
                value={snowAvg}
                onChange={(v) => setSnowAvg(v)}
              />
            </div>
            <InputField
              label="최대 풍속 (m/s)"
              type="number"
              value={windMax}
              onChange={(v) => setWindMax(v)}
            />
          </Section>

          {/* 3. 작업 사항 */}
          <Section
            title="작업 사항"
            expanded={expandedSections.work}
            onToggle={() => toggleSection("work")}
            icon={<FileText size={16} className="text-emerald-500" />}
          >
            {/* 확정 작업사항 + 상세작업 버튼 */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-gray-500 text-xs">확정 작업사항</label>
                <button
                  type="button"
                  onClick={() => setShowDetailModal(true)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-600 text-xs font-medium hover:bg-blue-100 transition-colors"
                >
                  <ClipboardList size={12} />
                  상세작업
                </button>
              </div>
              <textarea
                value={todayWork}
                onChange={(e) => setTodayWork(e.target.value)}
                placeholder="오늘 수행한 작업 내용을 입력하세요"
                rows={4}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
            <div>
              <label className="text-gray-500 text-xs mb-1 block">예정 작업사항</label>
              <textarea
                value={tomorrowWork}
                onChange={(e) => setTomorrowWork(e.target.value)}
                placeholder="내일 예정된 작업 내용을 입력하세요"
                rows={4}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none resize-none"
              />
            </div>
          </Section>

          {/* 4. 자재 현황 */}
          <Section
            title="자재 현황"
            expanded={expandedSections.material}
            onToggle={() => toggleSection("material")}
            icon={<Package size={16} className="text-purple-500" />}
            badge={`${summary.materialCount}개`}
          >
            <div className="space-y-3">
              {materialData.map((item, idx) => {
                const total = (Number(item.prev_total) || 0) + (Number(item.today) || 0);
                return (
                  <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">자재 {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeMaterial(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => updateMaterial(idx, "name", e.target.value)}
                      placeholder="자재명"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={item.spec}
                        onChange={(e) => updateMaterial(idx, "spec", e.target.value)}
                        placeholder="규격"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={item.unit}
                        onChange={(e) => updateMaterial(idx, "unit", e.target.value)}
                        placeholder="단위"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">전일누계</label>
                        <input
                          type="number"
                          value={item.prev_total}
                          onChange={(e) => updateMaterial(idx, "prev_total", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">금일</label>
                        <input
                          type="number"
                          value={item.today}
                          onChange={(e) => updateMaterial(idx, "today", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">누계</label>
                        <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-blue-50 text-sm text-center font-medium text-blue-700">
                          {total}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addMaterial}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <Plus size={18} />
              <span className="text-sm font-medium">자재 추가</span>
            </button>
          </Section>

          {/* 5. 장비 현황 */}
          <Section
            title="장비 현황"
            expanded={expandedSections.equipment}
            onToggle={() => toggleSection("equipment")}
            icon={<Wrench size={16} className="text-orange-500" />}
            badge={`${summary.equipmentCount}개`}
          >
            <div className="space-y-3">
              {equipmentData.map((item, idx) => {
                const total = (Number(item.prev_total) || 0) + (Number(item.today) || 0);
                return (
                  <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">장비 {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeEquipment(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={item.name}
                      onChange={(e) => updateEquipment(idx, "name", e.target.value)}
                      placeholder="장비명"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={item.spec}
                      onChange={(e) => updateEquipment(idx, "spec", e.target.value)}
                      placeholder="규격"
                      className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">전일누계</label>
                        <input
                          type="number"
                          value={item.prev_total}
                          onChange={(e) => updateEquipment(idx, "prev_total", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">금일</label>
                        <input
                          type="number"
                          value={item.today}
                          onChange={(e) => updateEquipment(idx, "today", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">누계</label>
                        <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-blue-50 text-sm text-center font-medium text-blue-700">
                          {total}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addEquipment}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <Plus size={18} />
              <span className="text-sm font-medium">장비 추가</span>
            </button>
          </Section>

          {/* 6. 인원 현황 */}
          <Section
            title="인원 현황"
            expanded={expandedSections.personnel}
            onToggle={() => toggleSection("personnel")}
            icon={<Users size={16} className="text-teal-500" />}
            badge={`${summary.personnelCount}명`}
          >
            <div className="space-y-3">
              {personnelData.map((item, idx) => {
                const total = (Number(item.prev_total) || 0) + (Number(item.today) || 0);
                return (
                  <div key={idx} className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-gray-600">인원 {idx + 1}</span>
                      <button
                        type="button"
                        onClick={() => removePersonnel(idx)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        value={item.work_type}
                        onChange={(e) => updatePersonnel(idx, "work_type", e.target.value)}
                        placeholder="공종"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                      <input
                        type="text"
                        value={item.category}
                        onChange={(e) => updatePersonnel(idx, "category", e.target.value)}
                        placeholder="구분"
                        className="w-full px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">전일누계</label>
                        <input
                          type="number"
                          value={item.prev_total}
                          onChange={(e) => updatePersonnel(idx, "prev_total", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-100 text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">금일</label>
                        <input
                          type="number"
                          value={item.today}
                          onChange={(e) => updatePersonnel(idx, "today", e.target.value)}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-center"
                        />
                      </div>
                      <div>
                        <label className="text-gray-400 text-[10px] block mb-0.5">누계</label>
                        <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-blue-50 text-sm text-center font-medium text-blue-700">
                          {total}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={addPersonnel}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <Plus size={18} />
              <span className="text-sm font-medium">인원 추가</span>
            </button>
          </Section>

          {/* 7. 첨부자료 */}
          <Section
            title="첨부자료"
            expanded={expandedSections.attachment}
            onToggle={() => toggleSection("attachment")}
            icon={<Paperclip size={16} className="text-gray-500" />}
          >
            <MobileAttachmentBox ref={attachmentRef} />
          </Section>
        </div>
      </main>

      {/* 모바일 하단 고정 액션바 */}
      <footer className="fixed inset-x-0 bottom-0 bg-white border-t border-gray-200 px-4 py-2.5 safe-area-bottom z-50">
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            className="w-full h-11 rounded-full border border-gray-300 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 active:scale-[0.97] transition-all"
            onClick={handleBack}
          >
            목록
          </button>
          <button
            type="button"
            className="w-full h-11 rounded-full bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 active:scale-[0.97] transition-all disabled:opacity-50"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "저장 중..." : "저장"}
          </button>
        </div>
      </footer>

      {/* 확인자 선택 모달 */}
      <ReferenceModal
        isOpen={showConfirmerModal}
        onClose={() => setShowConfirmerModal(false)}
        selectedIds={confirmerIds}
        name="확인자 선택"
        onChange={(ids) => {
          const onlyOne = ids.slice(-1);
          setConfirmerIds(onlyOne);
        }}
        onSave={(ids, detailUsers) => {
          const u = detailUsers?.[0];
          if (u) {
            setConfirmer(u.last_name + u.first_name);
            setConfirmerId(u.id);
          }
          return true;
        }}
      />

      {/* 상세작업 선택 모달 */}
      {showDetailModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white shadow-xl animate-slide-up">
            {/* 핸들 바 */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>

            {/* 헤더 */}
            <div className="px-5 pb-3 border-b border-slate-100">
              <h2 className="text-[18px] font-bold text-slate-900">상세작업 선택</h2>
              <p className="mt-0.5 text-[13px] text-slate-500">
                선택한 작업은 "확정 작업사항"에 자동 추가됩니다.
              </p>
            </div>

            {/* 목록 */}
            <div className="px-4 py-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {existingDetails.length ? (
                existingDetails.map((w) => (
                  <label
                    key={w.id}
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-200 p-3 hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      className="mt-1 w-4 h-4 rounded border-gray-300"
                      checked={selectedActivities.includes(w.activity)}
                      onChange={() =>
                        setSelectedActivities((prev) =>
                          prev.includes(w.activity)
                            ? prev.filter((id) => id !== w.activity)
                            : [...prev, w.activity]
                        )
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">
                          {w.activity_name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {w.created_by_name}
                        </span>
                      </div>
                      {w.content && (
                        <p className="mt-1 text-xs text-gray-600 whitespace-pre-line line-clamp-2">
                          {w.content}
                        </p>
                      )}
                    </div>
                  </label>
                ))
              ) : (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-400">저장된 상세작업이 없습니다.</p>
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="px-4 pb-4 pt-2 flex gap-3">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-medium active:scale-[0.97] transition-all"
              >
                취소
              </button>
              <button
                onClick={applySelectedDetails}
                className="flex-1 py-3 rounded-full bg-blue-500 text-white text-sm font-medium active:scale-[0.97] transition-all"
              >
                적용
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 이전 작업 불러오기 모달 */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="w-full max-w-lg max-h-[80vh] overflow-y-auto rounded-t-3xl bg-white shadow-xl animate-slide-up">
            {/* 핸들 바 */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>

            {/* 헤더 */}
            <div className="px-5 pb-3 border-b border-slate-100">
              <h2 className="text-[18px] font-bold text-slate-900">이전 공사일보 선택</h2>
              <p className="mt-0.5 text-[13px] text-slate-500">
                불러올 공사일보를 선택하세요. 작업 정보가 자동으로 채워집니다.
              </p>
            </div>

            {/* 목록 */}
            <div className="px-4 py-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {historyLoading ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-400">불러오는 중...</p>
                </div>
              ) : historyRows.length === 0 ? (
                <div className="py-8 text-center">
                  <p className="text-sm text-gray-400">불러올 공사일보가 없습니다.</p>
                </div>
              ) : (
                historyRows.map((row) => (
                  <button
                    key={row.dns}
                    onClick={() => handleSelectHistory(row)}
                    className="w-full flex items-center justify-between rounded-xl border border-gray-200 p-3 hover:bg-gray-50 active:scale-[0.98] transition-all text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-800">
                          {row.meta?.date
                            ? new Date(row.meta.date).toLocaleDateString("ko-KR")
                            : "-"}
                        </span>
                        <span className="text-xs text-gray-400">
                          {row.meta?.weather ?? ""}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        작성자: {row.created_by?.name ?? "-"} · 확인자: {row.confirmer ?? "-"}
                      </p>
                    </div>
                    <span className="text-gray-400 text-lg">›</span>
                  </button>
                ))
              )}
            </div>

            {/* 버튼 */}
            <div className="px-4 pb-4 pt-2">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="w-full py-3 rounded-full bg-gray-100 text-gray-700 text-sm font-medium active:scale-[0.97] transition-all"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 섹션 카드 컴포넌트
function Section({ title, expanded, onToggle, badge, icon, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100"
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs">
              {badge}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""
            }`}
        />
      </button>
      {expanded && (
        <div className="p-4 space-y-3">{children}</div>
      )}
    </section>
  );
}

// 입력 필드 컴포넌트
function InputField({ label, value, onChange, type = "text", placeholder, disabled }) {
  return (
    <div>
      <label className="text-gray-500 text-xs mb-1 block">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm focus:border-gray-400 focus:outline-none transition-colors disabled:bg-gray-100 disabled:text-gray-400"
      />
    </div>
  );
}